import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import fileDownload from 'react-file-download';

import { POLLING_INTERVAL, TERMINAL_STATES } from '~/components/Vendors/Home/RatePlansBulkUpload/constants';
import { BulkUploadStatus, ValidationResult } from '~/components/Vendors/Home/RatePlansBulkUpload/types';

import ReservationService from '~/services/ReservationService';

import { reservationQueryKeys } from './reservationQueryKeys';

function mapUploadStatus(upload: Record<string, unknown>): BulkUploadStatus {
  return {
    batchId: upload.batch_id as string,
    state: upload.state as BulkUploadStatus['state'],
    totalRows: upload.total_rows as number,
    processedRows: upload.processed_rows as number,
    successCount: upload.success_count as number,
    errorCount: upload.error_count as number,
    validRowsCount: (upload.valid_rows_count ?? 0) as number,
    createdAt: upload.created_at as string,
    results: (upload.results ?? []) as BulkUploadStatus['results'],
  };
}

function mapValidationResult(apiResponse: Record<string, unknown>): ValidationResult {
  const result = apiResponse.result as Record<string, unknown>;
  return {
    batchId: result.batch_id as string,
    phase: result.phase as ValidationResult['phase'],
    totalRows: result.total_rows as number,
    validCount: result.valid_count as number,
    errorCount: result.error_count as number,
    validationResults: (result.validation_results ?? []) as ValidationResult['validationResults'],
  };
}

export function useGetBulkUploadStatusList() {
  return useQuery({
    queryKey: reservationQueryKeys.ratePlanBulkUploadStatus(),
    queryFn: async () => {
      const response = await ReservationService.bulkUploadStatusList();
      const res = response as Record<string, unknown>;
      const resultObj = res.result as Record<string, unknown> | undefined;
      const uploads = (resultObj?.uploads ?? []) as Array<Record<string, unknown>>;
      return uploads.map(mapUploadStatus);
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.length === 0) {
        return false;
      }
      const hasInProgress = data.some((upload: BulkUploadStatus) => !TERMINAL_STATES.includes(upload.state));
      return hasInProgress ? POLLING_INTERVAL : false;
    },
  });
}

export function useValidateRatePlansBulkUpload() {
  return useMutation({
    mutationFn: async (file: File) => {
      const apiResponse = await ReservationService.validateRatePlansBulkUpload(file);
      return mapValidationResult(apiResponse);
    },
  });
}

export function useCommitRatePlansBulkUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (batchId: string) => {
      return await ReservationService.commitRatePlansBulkUpload(batchId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reservationQueryKeys.ratePlanBulkUploadStatus() });
    },
  });
}

export function useDownloadRatePlansBulkTemplate() {
  return useMutation({
    mutationFn: async (format: 'xlsx' | 'csv') => {
      const blob = await ReservationService.downloadRatePlansBulkTemplate(format);
      const fileName = `rate-plans-bulk-upload-template.${format}`;
      await fileDownload(blob, fileName);
    },
  });
}
